import { useCallback, useEffect, useRef, useState } from 'react'

import type { GameProps } from '../GameShell'

const PADS = [
  { id: 0, tone: 329.63, base: 'bg-positive/40', lit: 'bg-positive' },
  { id: 1, tone: 415.3, base: 'bg-negative/40', lit: 'bg-negative' },
  { id: 2, tone: 493.88, base: 'bg-brand/40', lit: 'bg-brand' },
  { id: 3, tone: 587.33, base: 'bg-muted/30', lit: 'bg-muted' },
]

const LIT_MS = 380
const GAP_MS = 140

/** A single sustained tone, longer than the sfx blips. */
function tone(freq: number, ms: number) {
  try {
    const ctx = new AudioContext()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = 'sine'
    osc.frequency.value = freq
    gain.gain.setValueAtTime(0.0001, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.01)
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + ms / 1000)
    osc.connect(gain).connect(ctx.destination)
    osc.start()
    osc.stop(ctx.currentTime + ms / 1000 + 0.05)
    // Contexts are a limited resource; release it once the note has rung out.
    setTimeout(() => void ctx.close(), ms + 200)
  } catch {
    // No audio available; the colours still carry the game.
  }
}

export default function Simon({ onGameOver, onScore }: GameProps) {
  const [sequence, setSequence] = useState<number[]>([])
  const [lit, setLit] = useState<number | null>(null)
  const [showing, setShowing] = useState(true)
  const [expecting, setExpecting] = useState(0)

  const timers = useRef<number[]>([])
  const clearTimers = () => {
    timers.current.forEach(clearTimeout)
    timers.current = []
  }
  useEffect(() => clearTimers, [])

  /** Play a sequence back, then hand control to the player. */
  const playBack = useCallback((steps: number[]) => {
    setShowing(true)
    clearTimers()

    steps.forEach((pad, i) => {
      const at = i * (LIT_MS + GAP_MS)
      timers.current.push(
        window.setTimeout(() => {
          setLit(pad)
          tone(PADS[pad].tone, LIT_MS)
        }, at),
      )
      timers.current.push(window.setTimeout(() => setLit(null), at + LIT_MS))
    })

    timers.current.push(
      window.setTimeout(
        () => {
          setShowing(false)
          setExpecting(0)
        },
        steps.length * (LIT_MS + GAP_MS) + 200,
      ),
    )
  }, [])

  const extend = useCallback(() => {
    setSequence((current) => {
      const next = [...current, Math.floor(Math.random() * PADS.length)]
      playBack(next)
      return next
    })
  }, [playBack])

  // First round.
  useEffect(() => {
    const id = window.setTimeout(extend, 500)
    return () => clearTimeout(id)
  }, [extend])

  function press(pad: number) {
    if (showing) return

    setLit(pad)
    tone(PADS[pad].tone, 220)
    window.setTimeout(() => setLit(null), 200)

    if (sequence[expecting] !== pad) {
      clearTimers()
      // Score is the number of full rounds survived, not the last partial one.
      onGameOver(sequence.length - 1)
      return
    }

    const next = expecting + 1
    if (next === sequence.length) {
      onScore?.(sequence.length)
      window.setTimeout(extend, 700)
    } else {
      setExpecting(next)
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-center text-sm text-muted">
        {showing ? 'Watch…' : `Repeat the sequence · round ${sequence.length}`}
      </p>

      <div className="mx-auto grid max-w-xs grid-cols-2 gap-3">
        {PADS.map((pad) => (
          <button
            key={pad.id}
            onClick={() => press(pad.id)}
            disabled={showing}
            aria-label={`Pad ${pad.id + 1}`}
            className={`aspect-square rounded-2xl transition-all duration-100 ${
              lit === pad.id ? `${pad.lit} scale-95` : pad.base
            } ${showing ? 'cursor-not-allowed' : 'hover:opacity-80'}`}
          />
        ))}
      </div>
    </div>
  )
}
